import React, { useEffect, useState } from "react";
import {
  requestGetConsultant,
  requestListConsultantTutoringProgram,
  useAuthState,
  useConsultantDispatch,
  useConsultantState,
  useTutoringProgramDispatch,
  useTutoringProgramState,
} from "../../Context";
import ConsultantTutoringCard from "../../Components/ConsultantTutoringCard";
import WebinarListSkeleton from "../../Components/WebinarListSkeleton";
import { Button } from "../../Components/Button";
import { useTranslation } from "react-i18next";
import { Pagination } from "../../Components/Pagination";
import { useNavigate } from "react-router-dom";
import { EmptyList } from "../../Components/EmptyList";
import { ErrorList } from "../../Components/ErrorList";

const ConsultantTutoringList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tutoringDispatch = useTutoringProgramDispatch();
  const consultantDispatch = useConsultantDispatch();

  const [page, setpage] = useState(1);
  const { tutoringList, tutoringListLoading, tutoringListError, nextTokens } =
    useTutoringProgramState();
  const { user } = useAuthState();
  const { consultant } = useConsultantState();

  useEffect(() => {
    if (user.id) {
      requestGetConsultant(consultantDispatch, { userID: user.id });
    }
  }, [consultantDispatch, user.id]);

  useEffect(() => {
    if (consultant.id) {
      requestListConsultantTutoringProgram(tutoringDispatch, {
        consultantID: consultant.id,
        page: 1,
        limit: 10,
      });
    }
  }, [tutoringDispatch, consultant.id]);

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      requestListConsultantTutoringProgram(tutoringDispatch, {
        page: nextPage,
        consultantID: consultant.id,
        limit: 10,
        nextToken: nextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListConsultantTutoringProgram(tutoringDispatch, {
        page: prevPage,
        consultantID: consultant.id,
        limit: 10,
        nextToken: nextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListConsultantTutoringProgram(tutoringDispatch, {
        page: selectedPage,
        consultantID: consultant.id,
        limit: 10,
        nextToken: nextTokens[selectedPage],
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {t("Tutoring Programs")}
          </h2>
          {!tutoringListLoading && tutoringList.length > 0 && (
            <p className="text-sm text-slate-500">
              {t("tutoringProgramCount", { count: tutoringList.length })}
            </p>
          )}
        </div>
        <Button
          className="sm:w-auto w-full"
          type="button"
          onClick={() => navigate("/manage-tutoring/create")}
          title={t("Create Tutoring Program")}
        />
      </div>
      <div>
        <div className="bg-white shadow-sm max-w-7xl mx-auto overflow-hidden px-4 py-6 sm:px-6 rounded-t-lg">
          {tutoringListLoading ? (
            <div className="grid animate-pulse">
              <WebinarListSkeleton totalItem={4} />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {tutoringList.length > 0 ? (
                <div className="-mx-px grid grid-cols-1 sm:grid-cols-2 sm:mx-0 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {tutoringList.map((item) => {
                    return (
                      <div
                        key={item.id}
                        className="flex border border-slate-200/70 rounded-xl p-4 transition-shadow hover:shadow-sm"
                      >
                        <ConsultantTutoringCard item={item} />
                      </div>
                    );
                  })}
                </div>
              ) : tutoringListError ? (
                <ErrorList
                  message={
                    tutoringListError?.message || "Error fetching class."
                  }
                  errors={tutoringListError?.errors}
                />
              ) : (
                <EmptyList
                  className="flex items-center justify-center"
                  title="No Tutoring Program"
                />
              )}
            </div>
          )}
        </div>
        <Pagination
          className="rounded-b-lg"
          buttonPosition="end"
          page={page}
          onPrev={onPrev}
          onNext={onNext}
          onPageChange={onPageChange}
          nextTokens={nextTokens}
        />
      </div>
    </div>
  );
};

export default ConsultantTutoringList;
